import { Injectable, inject } from '@angular/core';
import { from, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { findUniqueMatchWithId, readDocumentWithId } from '../common/firestore-utils';
import {Tenant, TenantInfo} from '../models/tenant.model';
import { DEFAULT_SCHOOL_ACCENT_COLOR, DEFAULT_SCHOOL_PRIMARY_COLOR, DEFAULT_SCHOOL_SALES_BANNER_COLOR } from '../common/ui-constants';
import { SubscriptionContent } from '../models/content/subscription-content.model';
import { DEFAULT_FAQS, DEFAULT_SUBSCRIPTION_BENEFITS } from './default.content';
import { TenantSettings } from '../models/tenant-settings.model';
import { TenantService } from './tenant.service';
import { EmailProviderSettings } from '../models/email-provider-settings.model';
import { HearAboutUsEvent } from '../authentication/auth-forms/hear-about-us/hear-about-us.event';
import { CookieService } from 'ngx-cookie-service';
import { collection, collectionData, deleteField, doc, docData, Firestore, limit, query, updateDoc, where, writeBatch } from '@angular/fire/firestore';
import { getDoc, setDoc, Timestamp } from '@firebase/firestore';
import {StudentEnrollmentSettings} from "../models/student-enrollment-settings.model";


@Injectable()
export class TenantsDBService {
  private cookies = inject(CookieService);
  private tenant = inject(TenantService);
  private firestore = inject(Firestore);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);



  constructor() {

  }

  /*
  *
  * If the tenant does not exist, create it, otherwise returns the existing tenant.
  *
  * */

  async createNewTenantWebsite(
    email: string,
    pictureUrl: string,
    displayName: string,
    createTestUser: boolean,
    subDomain: string,
    schoolName: string,
    referralSource: HearAboutUsEvent,
    allTenantsForEmail: Tenant[],
    userId: string,
    instructorStripeCustomerId: string): Promise<Tenant> {

    console.log(`Creating new tenant from scratch for user ${userId} with email ${email}, subdomain ${subDomain}`);

    const trackingInfo = this.extractTrackingInfo();

    console.log(`allTenantsForEmail: `, JSON.stringify(allTenantsForEmail))

    // an admin user can create multiple tenants for the same email. Each tenant is numbered.
    // seqNo is parsed from the END of the id (anchored) so a stray "-<digits>" earlier in the
    // string can't be mistaken for it, and unparseable/legacy ids are filtered out rather than
    // letting a single bad entry turn Math.max(...) into NaN, which used to fall back to 1 and
    // could collide with an existing tenant.
    const existingSeqNos = (allTenantsForEmail ?? [])
      .map(tenant => parseInt(tenant.id.match(/-(\d+)$/)?.[1], 10))
      .filter(seqNo => !isNaN(seqNo));

    console.log(`Existing tenant sequence numbers for ${email}: ${JSON.stringify(existingSeqNos)}`);

    let tenantIdIndex = existingSeqNos.length ? Math.max(...existingSeqNos) + 1 : 1;

    // Belt-and-suspenders: allTenantsForEmail can be stale or incomplete (e.g. a tenant whose
    // email field no longer matches the current query), which previously let a computed id
    // collide with an existing tenant and silently overwrite it — batch.set() below replaces
    // the whole document. Never write to an id that's already taken.
    let tenantId = `${userId}-${tenantIdIndex}`;

    while ((await getDoc(doc(this.firestore, `tenants/${tenantId}`))).exists()) {
      console.warn(`Tenant id ${tenantId} already exists — incrementing to avoid overwriting it.`);
      tenantIdIndex++;
      tenantId = `${userId}-${tenantIdIndex}`;
    }

    console.log(`Creating new tenant with id ${tenantId}`);

    /**
     *
     * versions:
     *
     * 1.0 initial version
     *
     */

    const newTenant: Partial<Tenant> = {
      id: tenantId,
      version: 1.0,
      email,
      status: 'new',
      contactPageDisabled: true,
      allLessonsTabOff: true,
      subDomain,
      schoolName,
      accountCreatedAt: Timestamp.fromMillis(new Date().getTime()),
      supportEmail: email,
      homePageOn: true,
      guaranteeOffered: true,
      guaranteeDays: 30,
      brandTheme: {
        primaryColor: DEFAULT_SCHOOL_PRIMARY_COLOR,
        accentColor: DEFAULT_SCHOOL_ACCENT_COLOR,
        salesPageBannerColor: DEFAULT_SCHOOL_SALES_BANNER_COLOR,
        salesPageListItemIconColor: DEFAULT_SCHOOL_PRIMARY_COLOR,
        salesPageTitleIconColor: DEFAULT_SCHOOL_PRIMARY_COLOR,
        themeCustomized: false,
      },
      totalEmailsCollected: 0,
      instructorPricingPlan: 'growth',
      referralSource: (referralSource?.source ?? referralSource.other) ?? null,
      referralSourceOther: referralSource?.other ?? null,
      instructorStripeCustomerId,
      utms: trackingInfo?.utms ?? null,
      storageUsedInMb: 0,
    };

    const newSchoolUser: Partial<Tenant> = {
      id: userId,
      email,
      displayName
    };

    if (pictureUrl) {
      newSchoolUser.pictureUrl = pictureUrl;
    }

    if (createTestUser || email?.endsWith('onlinecoursehost.com') || document.location.host?.includes('onlinecoursehost-tests.com')) {
      newSchoolUser.testUser = true;
    }

    const batch = writeBatch(this.firestore)

    const schoolUserRef = doc(this.firestore, `schools/${tenantId}/users/${userId}`);
    batch.set(schoolUserRef, newSchoolUser);

    const tenantRef = doc(this.firestore, `tenants/${tenantId}`);
    batch.set(tenantRef, newTenant);

    // the tenant is also an admin in its own website
    const permissionsPath = `tenantSettings/${tenantId}/userPermissions/${userId}`

    console.log(`Creating permissions at ${permissionsPath}`);

    const permissionsRef = doc(this.firestore, permissionsPath);

    // this is not a security issue - only the tenant himself can do this write from the client
    batch.set(permissionsRef, { isAdmin: true, email, displayName });

    // set default tenant website content
    const defaultSubscriptionContent: SubscriptionContent = {
      subscriptionBenefits: DEFAULT_SUBSCRIPTION_BENEFITS,
      faqs: DEFAULT_FAQS
    };

    const subscriptionContentRef = doc(this.firestore, `schools/${tenantId}/content/subscription`);
    batch.set(subscriptionContentRef, defaultSubscriptionContent as any);

    await batch.commit();

    return newTenant as Tenant;
  }

  findTenantById(tenantId: string) {
    const docRef = doc(this.firestore, `tenants/${tenantId}`)
    return docData(docRef).pipe(
      first()
    ) as Observable<Tenant>;
  }

  findTenantBySubdomain(subDomain: string): Observable<Tenant> {

    const constraints = [
      where('subDomain', '==', subDomain),
      limit(1)
    ]

    const tenantsQuery$ = query(
      collection(this.firestore, 'tenants'),
      ...constraints
    )

    const query$ = collectionData(tenantsQuery$);

    return findUniqueMatchWithId(query$).pipe(first()) as Observable<Tenant>;
  }

  findTenantByCustomDomain(customDomain: string): Observable<Tenant> {
    const constraints = [
      where('customDomain', '==', customDomain),
      limit(1)
    ]

    const tenantsQuery$ = query(
      collection(this.firestore, 'tenants'),
      ...constraints
    )

    const query$ = collectionData(tenantsQuery$);

    return findUniqueMatchWithId(query$).pipe(first()) as Observable<Tenant>
  }

  saveTheme(tenantId: string, primaryColor: string, accentColor: string, salesPageBannerColor: string, salesPageTitleIconColor: string, salesPageListItemIconColor: string ): Observable<any> {

    const tenantCollectionRef = collection(this.firestore, 'tenants');

    const tenantDocRef = doc(tenantCollectionRef, tenantId);

    return from(updateDoc(tenantDocRef, { brandTheme: { primaryColor, accentColor, salesPageBannerColor, salesPageTitleIconColor, salesPageListItemIconColor } }))

  }

  findCustomSubDomain(subDomain: string) {

    const constraints = [
      where('subDomain', '==', subDomain)
    ]

    const customSubDomainQuery = query(collection(this.firestore, `tenants`), ...constraints)

    const customSubDomainSnap = collectionData(customSubDomainQuery)

    return customSubDomainSnap.pipe(
      map(results => results.length == 1 ? results[0] : null),
      first()
    );
  }

  updateStudentEnrollmentSettings(tenantId: string, changes:  Partial<StudentEnrollmentSettings>, tenant: TenantInfo) {

    const batch = writeBatch(this.firestore);

    const tenantRef = doc(this.firestore, `tenants/${tenantId}`)

    const tenantUpdate = {
      ...tenant,
      studentEnrollmentSettings: {
        ...tenant.studentEnrollmentSettings,
        ...changes
      },
    }

    batch.update(tenantRef, tenantUpdate);

    return from(batch.commit());

  }

  updateTenant(tenantId: string, changes: Partial<Tenant>): Observable<void> {

    const batch = writeBatch(this.firestore);

    const tenantRef = doc(this.firestore, `tenants/${tenantId}`)

    batch.update(tenantRef, changes);

    if (changes?.zapierConfig?.integrationToken && changes.zapierConfig.enabled) {

      const zapierIntegrationRef = doc(this.firestore, `zapierIntegrationTokens/${changes.zapierConfig.integrationToken}`)

      batch.set(zapierIntegrationRef, {
        tenantId: this.tenant.id
      },
        { merge: true });
    }

    // save pabbly integration token
    if (changes?.pabblyConfig?.integrationToken && changes.pabblyConfig.enabled) {

      const pabblyIntegrationRef = doc(this.firestore, `pabblyIntegrationTokens/${changes.pabblyConfig.integrationToken}`);

      batch.set(pabblyIntegrationRef, {
        tenantId: this.tenant.id
      },
        { merge: true });
    }

    // save zoom integration token
    if (changes?.zoomConfig?.accessToken && changes.zoomConfig.enabled) {
      const oldToken = this.tenant.tenant?.zoomConfig?.accessToken;
      const newToken = changes.zoomConfig.accessToken;

      if (oldToken && oldToken !== newToken) {
        const oldTokenRef = doc(this.firestore, `zoomIntegrationTokens/${oldToken}`);
        batch.delete(oldTokenRef);
      }

      const zoomIntegrationRef = doc(this.firestore, `zoomIntegrationTokens/${newToken}`);

      batch.set(zoomIntegrationRef, {
        tenantId: this.tenant.id
      },
        { merge: true });
    }

    return from(batch.commit());
  }

  loadTenantPrivateSettings(): Observable<TenantSettings> {
    return readDocumentWithId<TenantSettings>(
      docData(doc(this.firestore, `tenantSettings/${this.tenant.id}`))).pipe(first());

  }

  updateTenantSettings(tenantId: string, changes: Partial<TenantSettings>): Observable<void> {
    return from(updateDoc(doc(this.firestore, `tenantSettings/${tenantId}`), changes));
  }

  activateEmailMarketingIntegration(emailProviderSettings: EmailProviderSettings): Observable<any> {

    console.log(`Called tenants db service activateEmailMarketingIntegration()`);

    const batch = writeBatch(this.firestore);

    const tenantSettingsRef = doc(this.firestore, `tenantSettings/${this.tenant.id}`),
      tenantRef = doc(this.firestore, `tenants/${this.tenant.id}`);

    batch.set(tenantSettingsRef, { emailProvider: emailProviderSettings }, { merge: true });

    batch.update(tenantRef, { isNewsletterIntegrationActive: true });

    return from(batch.commit());

  }

  cancelEmailMarketingIntegration() {

    const batch = writeBatch(this.firestore);

    const tenantSettingsRef = doc(this.firestore, `tenantSettings/${this.tenant.id}`),
      tenantRef = doc(this.firestore, `tenants/${this.tenant.id}`);

    batch.update(tenantSettingsRef, { emailProvider: <any>deleteField() });

    batch.update(tenantRef, { isNewsletterIntegrationActive: false });

    return from(batch.commit());
  }

  /**
   *
   * returns all the tenants matching a single email. The same email could be used to manage the accounts of several tenants.
   *
   */

  async findAllTenantsByEmail(email: string) {

    const constraints = [
      where('email', '==', email)
    ]

    const tenantsByEmailQuery = query(collection(this.firestore, 'tenants'), ...constraints)

    const querySnapshot = await collectionData(tenantsByEmailQuery, { idField: 'id' }).pipe(first()).toPromise()

    if (querySnapshot.length == 0) {
      return [];
    }

    const allTenantsForEmail = querySnapshot;

    return allTenantsForEmail as Tenant[];
  }

  extractTrackingInfo() {

    const payload = this.cookies.get("OCH_SOURCES");

    if (payload) {

      try {

        const ochSources = JSON.parse(payload);

        console.log("OCH_SOURCES cookie parsed", ochSources);

        return ochSources;
      }
      catch (error) {
        console.error("Error parsing OCH_SOURCES cookie", error);
        return null;
      }
    }
  }

  getTenantSettings(tenantId: string): Promise<TenantSettings> {
    const tenantSettingsRef = doc(this.firestore, `tenantSettings/${tenantId}`);
    return getDoc(tenantSettingsRef).then(snapshot => {
      if (snapshot.exists()) {
        return snapshot.data() as TenantSettings;
      } else {
        throw new Error(`No settings found for tenant ID: ${tenantId}`);
      }
    });
  }

  updateAddTenantSettings(tenantId: string, changes: Partial<TenantSettings>): Observable<void> {
    const docRef = doc(this.firestore, `tenantSettings/${tenantId}`);
    return from(setDoc(docRef, changes, { merge: true }));
  }
}








